import { useEffect, useRef } from 'react';
import { View, Text, ScrollView, TouchableOpacity } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import {
  LAST_ONBOARDING_STEP,
  LAST_ONBOARDING_STEP_INDEX,
  ONBOARDING_ROUTES,
  ONBOARDING_TOTAL_STEPS,
  getOnboardingStepValueForVisibleIndex,
} from '@perdiem/trpc/onboarding';
import { supabase } from '../lib/supabase';
import { logOutRevenueCat } from '../lib/purchases';
import {
  getOnboardingStepLabel,
  trackAuthSignedOut,
  trackProductEvent,
} from '../lib/product-events';
import { trpc } from '../lib/trpc';
import { ProgressBar } from './ProgressBar';
import { useUnregisterPushNotifications } from '../hooks/usePushNotifications';

interface OnboardingShellProps {
  step: number;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  scrollable?: boolean;
  showBack?: boolean;
  backRoute?: string;
  backStoredStep?: number;
  backDisabled?: boolean;
}

export function OnboardingShell({
  step,
  title,
  subtitle,
  children,
  footer,
  scrollable = true,
  showBack = true,
  backRoute,
  backStoredStep,
  backDisabled = false,
}: OnboardingShellProps) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useLocalSearchParams<{ returnToChecklist?: string | string[] }>();
  const utils = trpc.useUtils();
  const updateOnboardingStep = trpc.households.updateOnboardingStep.useMutation();
  const unregisterPushNotifications = useUnregisterPushNotifications();
  const reportedStepRef = useRef<string | null>(null);
  const canGoBack = showBack && step > 0 && !backDisabled;
  const returnToChecklistRequested = Array.isArray(params.returnToChecklist)
    ? params.returnToChecklist[0] === '1'
    : params.returnToChecklist === '1';
  const shouldShowChecklistReturn = returnToChecklistRequested && step < LAST_ONBOARDING_STEP_INDEX;

  const withChecklistReturnParam = (route: string) => {
    if (!shouldShowChecklistReturn) {
      return route;
    }

    return `${route}${route.includes('?') ? '&' : '?'}returnToChecklist=1`;
  };

  useEffect(() => {
    const stepNumber = step + 1;
    const stepLabel = getOnboardingStepLabel(stepNumber);
    const reportKey = `${pathname}:${stepNumber}:${title}`;

    if (reportedStepRef.current === reportKey) {
      return;
    }

    reportedStepRef.current = reportKey;

    trackProductEvent({
      eventName: 'onboarding.step_viewed',
      route: pathname,
      screen: 'onboarding',
      component: 'OnboardingShell',
      message: title,
      stepNumber,
      stepLabel,
      metadata: {
        title,
        ...(subtitle ? { subtitle } : {}),
      },
    });
  }, [pathname, step, subtitle, title]);

  const handleSignOut = async () => {
    trackAuthSignedOut({
      route: pathname,
      source: 'onboarding_shell',
    });
    await unregisterPushNotifications();
    await logOutRevenueCat();
    await supabase.auth.signOut();
    router.replace('/(auth)/login');
  };

  const handleBack = async () => {
    if (!canGoBack || updateOnboardingStep.isPending) {
      return;
    }

    const previousVisibleStep = step - 1;
    const previousRoute = backRoute ?? ONBOARDING_ROUTES[previousVisibleStep] ?? ONBOARDING_ROUTES[0];
    const previousStoredStep = backStoredStep ?? getOnboardingStepValueForVisibleIndex(previousVisibleStep);

    await updateOnboardingStep.mutateAsync({ step: previousStoredStep });
    await utils.households.get.invalidate();
    router.replace(withChecklistReturnParam(previousRoute) as any);
  };

  const handleReturnToChecklist = async () => {
    if (!shouldShowChecklistReturn || updateOnboardingStep.isPending) {
      return;
    }

    await updateOnboardingStep.mutateAsync({ step: LAST_ONBOARDING_STEP });
    await utils.households.get.invalidate();
    router.replace(ONBOARDING_ROUTES[LAST_ONBOARDING_STEP_INDEX] as any);
  };

  return (
    <SafeAreaView
      testID={`onboarding-step-${step + 1}-screen`}
      accessibilityLabel={`Onboarding step ${step + 1}: ${title}`}
      className="flex-1 bg-canvas dark:bg-canvas-dark"
    >
      {/* Progress */}
      <View className="px-6 pt-4 pb-2">
        <View className="flex-row justify-between mb-2">
          {canGoBack ? (
            <TouchableOpacity onPress={handleBack} hitSlop={8} disabled={updateOnboardingStep.isPending}>
              <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {updateOnboardingStep.isPending ? 'Going back...' : '← Back'}
              </Text>
            </TouchableOpacity>
          ) : (
            <View />
          )}
          <TouchableOpacity onPress={handleSignOut} hitSlop={8}>
            <Text className="text-xs text-gray-400 dark:text-gray-500">Sign out</Text>
          </TouchableOpacity>
        </View>
        <View className="flex-row items-center justify-between mb-2">
          <Text className="text-xs text-gray-400 dark:text-gray-500">
            Step {step + 1} of {ONBOARDING_TOTAL_STEPS}
          </Text>
          {shouldShowChecklistReturn && (
            <TouchableOpacity
              onPress={handleReturnToChecklist}
              hitSlop={8}
              disabled={updateOnboardingStep.isPending}
            >
              <Text className="text-xs font-medium text-forest-600 dark:text-forest-500">
                {updateOnboardingStep.isPending ? 'Returning...' : 'Back to checklist'}
              </Text>
            </TouchableOpacity>
          )}
        </View>
        <ProgressBar value={(step + 1) / ONBOARDING_TOTAL_STEPS} />
      </View>

      {scrollable ? (
        <ScrollView className="flex-1 px-6 pt-6" contentContainerStyle={{ paddingBottom: 32 }}>
          <Text className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">{title}</Text>
          {subtitle && <Text className="text-sm text-gray-500 dark:text-gray-400 mb-6">{subtitle}</Text>}
          {children}
        </ScrollView>
      ) : (
        <View className="flex-1 px-6 pt-6">
          <Text className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">{title}</Text>
          {subtitle && <Text className="text-sm text-gray-500 dark:text-gray-400 mb-6">{subtitle}</Text>}
          <View className="flex-1">{children}</View>
        </View>
      )}

      {footer && (
        <View className="px-6 pb-8 pt-4 border-t border-gray-100 dark:border-gray-700">{footer}</View>
      )}
    </SafeAreaView>
  );
}
